import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { 
  Newspaper,
  Funnel,
  CaretRight,
  CaretLeft,
  CircleNotch,
  WarningCircle,
  Check,
  Clock,
  ArrowsClockwise,
  Question,
  User,
  ArrowsDownUp,
  ChatCircle,
  PushPin,
  PushPinSlash,
  Chat,
  X,
  MagnifyingGlass,
  Envelope,
  Lightning,
  Robot,
  Tray,
  CaretDown,
  CaretUp,
  Calendar,
  Copy,
  Tray as Inbox,
  Archive,
  Star,
  UserPlus,
  FileText,
  Download,
  PlusCircle,
  ArrowLeft,
  ArrowRight,
  DotsThreeVertical,
  Trash,
  Repeat,
  ArrowCounterClockwise,
  ArrowSquareOut
} from '@phosphor-icons/react';
import { Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useClickAway } from 'react-use';
import useHelpPanel from '../hooks/use-help-panel';
import HelpPanel from '../components/HelpPanel';
import CallDetailsPage from '../components/calls/CallDetailsPage';
import TextDetailsPage from '../components/texts/TextDetailsPage';
import ChatConversationDetailsPage from '../components/chats/ChatConversationDetailsPage';

// Simplified components for split view
const CallDetailsWrapper = ({ callId, callData }) => {
  return (
    <Card className="overflow-hidden border-0 shadow-sm bg-white h-full">
      <CardContent className="p-8 h-full flex flex-col">
        <div className="text-center flex-1 flex flex-col justify-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
            <Phone className="w-8 h-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Call Details</h3>
          <p className="text-gray-600 mb-6">
            {callData?.summary ? callData.summary.substring(0, 150) + '...' : 'Call selected'}
          </p>
          <div className="space-y-3 text-sm text-gray-500">
            <div className="flex justify-between">
              <span>Caller:</span>
              <span className="font-medium">{callData?.contact?.name || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span>Date:</span>
              <span className="font-medium">{callData?.created_at ? formatCallDate(callData.created_at) : 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span>Status:</span>
              <span className="font-medium">{callData?.call_category?.replace('_', ' ') || 'Unknown'}</span>
            </div>
          </div>
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 text-center">
              Full call details with recording, transcript, and action items are available on mobile or by clicking the item on smaller screens.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TextDetailsWrapper = ({ conversationId, conversationData }) => {
  return (
    <Card className="overflow-hidden border-0 shadow-sm bg-white h-full">
      <CardContent className="p-8 h-full flex flex-col">
        <div className="text-center flex-1 flex flex-col justify-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-50 flex items-center justify-center">
            <ChatCircle className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Text Conversation</h3>
          <p className="text-gray-600 mb-6">
            {conversationData?.last_message ? conversationData.last_message.substring(0, 150) + '...' : 'Conversation selected'}
          </p>
          <div className="space-y-3 text-sm text-gray-500">
            <div className="flex justify-between">
              <span>Contact:</span>
              <span className="font-medium">{conversationData?.contact?.name || 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Message:</span>
              <span className="font-medium">{conversationData?.updated_at ? formatCallDate(conversationData.updated_at) : 'Unknown'}</span>
            </div>
            <div className="flex justify-between">
              <span>Messages:</span>
              <span className="font-medium">{conversationData?.message_count || 0}</span>
            </div>
          </div>
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 text-center">
              Full conversation details with message history and reply functionality are available on mobile or by clicking the item on smaller screens.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Skeleton loader for right panel details
const DetailsSkeleton = () => (
  <Card className="overflow-hidden border-0 shadow-xl bg-white h-full flex flex-col rounded-2xl">
    <CardContent className="p-0 h-full flex flex-col">
      {/* Skeleton Header */}
      <div className="p-5 sm:p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
        <div className="flex items-start justify-between mb-4">
          <div className="text-right">
            <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>

      {/* Skeleton Action Items */}
      <div className="p-5 sm:p-6 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse mt-1"></div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="flex-1">
              <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Skeleton Summary */}
      <div className="p-5 sm:p-6 border-b border-gray-100 flex-shrink-0">
        <div className="space-y-2">
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-4/5 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-3/5 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>

      {/* Skeleton Recording Controls */}
      <div className="p-5 sm:p-6 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Skeleton Transcript */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4">
          <div className="space-y-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex gap-3">
                <div className="w-7 h-7 bg-gray-200 rounded-xl animate-pulse flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <div className="h-3 w-16 bg-gray-200 rounded animate-pulse mb-1"></div>
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Simple skeleton loader component
const SkeletonItem = () => (
  <div className="py-3 px-4 md:py-2.5 md:px-3">
    <div className="flex items-center gap-3">
      <div className="w-1.5 h-1.5 bg-gray-200 rounded-full animate-pulse" />
      <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
      <div className="flex-1">
        <div className="flex justify-between items-start mb-1">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-16" />
        </div>
        <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
      </div>
      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse md:hidden" />
    </div>
  </div>
);

// Help panel content for the Calls pages
export const helpPanelSections = [
  {
    id: "calls-overview",
    title: "About Your Activity Feed",
    content: `
      <div class="space-y-4">
        <p>Your activity feed provides a complete record of every interaction your AI assistant has with customers, including calls, texts, recordings, transcripts, and follow-up items.</p>
        
        <div class="flex items-start p-3 bg-blue-50 rounded-lg">
          <div class="mr-3 mt-0.5 text-blue-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
          </div>
          <div>
            <p class="text-blue-800 font-medium">Never miss important customer details</p>
            <p class="text-blue-600 text-sm mt-1">Each interaction is automatically recorded, transcribed, and organized—letting you review conversations and take action when needed.</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "call-status",
    title: "Understanding Call Status",
    content: `
      <div class="space-y-4">
        <p>Calls are automatically categorized to help you quickly identify which ones need your attention:</p>
        
        <div class="space-y-3 mt-4">
          <div class="p-3 border border-emerald-100 rounded-lg bg-emerald-50">
            <h4 class="text-sm font-medium text-emerald-800 mb-1 flex items-center">
              <svg class="w-4 h-4 mr-2" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4"/>
                <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Resolved
            </h4>
            <p class="text-emerald-600 text-sm ml-6">
              All action items have been completed. No further attention needed.
            </p>
          </div>
          
          <div class="p-3 border border-amber-100 rounded-lg bg-amber-50">
            <h4 class="text-sm font-medium text-amber-800 mb-1 flex items-center">
              <svg class="w-4 h-4 mr-2" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4"/>
                <path d="M8 4.5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="8" cy="12" r="1" fill="currentColor"/>
              </svg>
              Action Required
            </h4>
            <p class="text-amber-600 text-sm ml-6">
              Contains incomplete action items that need your attention. Click to see what needs to be done.
            </p>
          </div>
          
          <div class="p-3 border border-slate-100 rounded-lg bg-slate-50">
            <h4 class="text-sm font-medium text-slate-800 mb-1 flex items-center">
              <svg class="w-4 h-4 mr-2" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4"/>
                <path d="M5 5L11 11M11 5L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Incomplete
            </h4>
            <p class="text-slate-600 text-sm ml-6">
              The call ended before the conversation was completed. May need follow-up.
            </p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "filtering-searching",
    title: "Finding Specific Calls",
    content: `
      <div class="space-y-4">
        <p>Quickly locate specific calls using the search and filter tools:</p>
        
        <h3 class="text-sm font-medium text-gray-900 mt-4 mb-2">Search</h3>
        <p class="text-gray-700 text-sm">
          Use the search bar to find calls by caller name or phone number.
        </p>
        
        <h3 class="text-sm font-medium text-gray-900 mt-4 mb-2">Filter</h3>
        <p class="text-gray-700 text-sm">
          Click the filter button to show only specific call types:
        </p>
        <ul class="list-disc list-inside space-y-1.5 text-sm text-gray-600 mt-2 ml-2">
          <li>Show all calls</li>
          <li>Only show calls with action items</li>
          <li>Only show resolved calls</li>
          <li>Only show incomplete calls</li>
        </ul>
        
        <div class="flex items-start p-3 bg-purple-50 rounded-lg mt-2">
          <div class="mr-3 mt-0.5 text-purple-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
          </div>
          <div>
            <p class="text-purple-800 font-medium">Pro tip</p>
            <p class="text-purple-600 text-sm mt-1">Start each day by filtering for "Action Required" calls to quickly handle any outstanding tasks.</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "call-details",
    title: "Call Details Page",
    content: `
      <div class="space-y-4">
        <p>Clicking on any call opens the detailed view, where you can:</p>
        
        <ul class="space-y-3 mt-4">
          <li class="flex gap-2">
            <div class="flex-shrink-0 h-6 w-6 bg-black rounded-full text-white flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </div>
            <div>
              <span class="font-medium text-slate-900 text-sm">Listen to the recording</span>
              <p class="text-slate-600 text-xs mt-0.5">Hear the full conversation between the caller and your AI assistant.</p>
            </div>
          </li>
          
          <li class="flex gap-2">
            <div class="flex-shrink-0 h-6 w-6 bg-black rounded-full text-white flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div>
              <span class="font-medium text-slate-900 text-sm">Complete action items</span>
              <p class="text-slate-600 text-xs mt-0.5">Mark tasks as done when you've completed them. This helps track what's finished.</p>
            </div>
          </li>
          
          <li class="flex gap-2">
            <div class="flex-shrink-0 h-6 w-6 bg-black rounded-full text-white flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-message-square"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div>
              <span class="font-medium text-slate-900 text-sm">Read the transcript</span>
              <p class="text-slate-600 text-xs mt-0.5">Review the written transcript of the entire conversation.</p>
            </div>
          </li>
        </ul>
        
        <div class="p-3 border border-blue-100 rounded-lg bg-blue-50 mt-2">
          <h4 class="text-sm font-medium text-blue-800 mb-1">Summary</h4>
          <p class="text-blue-600 text-sm">
            The call summary at the top provides a quick overview of what the call was about, making it easy to understand the purpose without listening to the entire recording.
          </p>
        </div>
      </div>
    `
  },
  {
    id: "staying-organized",
    title: "Staying Organized",
    content: `
      <div class="space-y-4">
        <p>Here are some best practices for efficiently managing your calls:</p>
        
        <ul class="list-disc list-inside space-y-2 text-sm text-gray-700 mt-3">
          <li>Complete action items promptly to keep your call list organized</li>
          <li>Use the "Refresh" button to see new calls that have come in</li>
          <li>Check the call timestamp to know exactly when the conversation occurred</li>
          <li>Search for a caller's phone number to find all their previous calls</li>
        </ul>
        
        <div class="mt-4 p-3 border border-amber-100 rounded-lg bg-amber-50">
          <h4 class="text-sm font-medium text-amber-800 mb-1 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-lightbulb mr-2"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
            Remember
          </h4>
          <p class="text-amber-600 text-sm">
            Your AI assistant will handle many routine calls entirely on its own. For those that need your attention, you'll find them clearly marked with an "Action Required" status.
          </p>
        </div>
      </div>
    `
  }
];

// More elegant and cinematic animations
const pageVariants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.08
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const cardVariants = {
  initial: { opacity: 0, y: 15 },
  animate: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const listItemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const buttonVariants = {
  rest: { scale: 1 },
  hover: { 
    scale: 1.02,
    transition: {
      duration: 0.15,
      ease: "easeOut"
    }
  },
  tap: { 
    scale: 0.98,
    transition: {
      duration: 0.08
    }
  }
};

const statusConfig = {
  issue_resolved: {
    color: 'text-emerald-600 border-emerald-200',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-100',
    label: 'Resolved'
  },
  action_required: {
    color: 'text-amber-600 border-amber-200',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-100',
    label: 'Action Required'
  },
  incomplete_call: {
    color: 'text-slate-600 border-slate-200',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-100',
    label: 'Incomplete'
  }
};

// Configuration for text conversation statuses
const conversationStatusConfig = {
  active: {
    color: 'text-blue-600 border-blue-200',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
    label: 'Active'
  },
  completed: {
    color: 'text-emerald-600 border-emerald-200',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-100',
    label: 'Completed'
  },
  pending: {
    color: 'text-amber-600 border-amber-200',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-100',
    label: 'Pending'
  }
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove any non-digit characters first
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle US/Canada numbers (with or without +1)
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    const number = cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
  }

  // Handle international numbers
  if (cleaned.length > 10) {
    // Extract country code (assume first 2-3 digits after any +1)
    const countryCode = cleaned.startsWith('1') ? cleaned.slice(1,3) : cleaned.slice(0,2);
    const remaining = cleaned.startsWith('1') ? cleaned.slice(3) : cleaned.slice(2);

    // Format remaining numbers
    return `+${countryCode} (${remaining.slice(0,3)}) ${remaining.slice(3,6)}-${remaining.slice(6)}`;
  }

  return phoneNumber; // Return original if we can't format it
};

const formatCallDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  
  // Format time consistently
  const timeString = date.toLocaleString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  }).toLowerCase();

  // Check if it's today (same year, month, and day)
  const isToday = date.getDate() === now.getDate() && 
                  date.getMonth() === now.getMonth() &&
                  date.getFullYear() === now.getFullYear();

  // If it's today
  if (isToday) {
    return timeString;
  }

  // If it's within the last 7 days
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    const dayOfWeek = date.toLocaleString('en-US', { weekday: 'long' });
    return `${dayOfWeek} at ${timeString}`;
  }

  // Otherwise show full date
  const monthDay = date.toLocaleString('en-US', { 
    month: 'long',
    day: 'numeric'
  });
  return `${monthDay} at ${timeString}`;
};

// Helper functions for text conversations
const getConversationDisplayName = (thread) => {
  // If contact exists and has a name
  if (thread.contact?.name) {
    const name = thread.contact.name;
    
    // Check if name contains "customer" (case-insensitive)
    if (name.toLowerCase().includes('customer')) {
      // Extract everything between the first "(" and last ")"
      const start = name.indexOf('(');
      const end = name.lastIndexOf(')');
      
      if (start !== -1 && end !== -1 && end > start) {
        // Return the content between parentheses, removing the parentheses themselves
        return name.substring(start + 1, end).trim();
      }
      
      // If no parentheses found, fall back to phone number
      return formatPhoneNumber(thread.contact.phone_number || thread.from_number || '');
    }
    
    return name;
  }
  
  // Fallback to formatted phone number if no contact
  return formatPhoneNumber(thread.from_number || thread.contact?.phone_number || '');
};

const getLastMessagePreview = (thread) => {
  if (!thread.last_message) return "No messages yet";
  
  // Truncate message if too long
  const maxLength = 50;
  if (thread.last_message.length > maxLength) {
    return `${thread.last_message.substring(0, maxLength)}...`;
  }
  return thread.last_message;
};

const getConversationStatus = (thread) => {
  if (!thread.updated_at) return 'pending';
  
  const lastUpdate = new Date(thread.updated_at);
  const now = new Date();
  const diffHours = Math.abs(now - lastUpdate) / 36e5; // hours
  
  if (diffHours < 24) return 'active';
  if (diffHours < 72) return 'pending';
  return 'completed';
};

// Function to get responsive character limit based on screen width
const getResponsiveCharLimit = () => {
  const width = window.innerWidth;
  
  if (width >= 1920) return 110;      // Full desktop (1920px+)
  if (width >= 1536) return 100;      // 2xl screens (1536px+)
  if (width >= 1280) return 90;       // xl screens (1280px+)
  if (width >= 1024) return 80;       // lg screens (1024px+)
  if (width >= 768) return 70;        // md screens (768px+)
  if (width >= 640) return 60;        // sm screens (640px+)
  return 50;                          // mobile (< 640px)
};

const hasIncompleteTasks = (item) => {
  if (!item.todo_items || !Array.isArray(item.todo_items)) return false;
  return item.todo_items.some(task => !task.is_completed);
};

const hasUrgentTasks = (item) => {
  if (!item.todo_items || !Array.isArray(item.todo_items)) return false;
  // Check if any incomplete task is marked as urgent or high priority
  return item.todo_items.some(task => 
    !task.is_completed && 
    (task.priority === 'urgent' || task.priority === 'high' || task.is_urgent === true)
  );
};

// Function to check if a call should be marked as viewed (all tasks completed)
const shouldBeViewed = (call) => {
  // If already viewed, keep it viewed
  if (call.viewed) return true;
  
  // For action required calls, only mark as viewed when all tasks are completed
  if (call.call_category === 'action_required') {
    if (!call.todo_items || !Array.isArray(call.todo_items) || call.todo_items.length === 0) {
      return true; // No tasks means "completed"
    }
    return call.todo_items.every(task => task.is_completed);
  }
  
  // For non-action required calls, don't auto-mark as viewed - wait for user click
  return false;
};

// Function to update calls that should be marked as viewed in the database
const updateViewedStatus = async (calls, supabase) => {
  const callsToUpdate = calls.filter(call => 
    !call.viewed && shouldBeViewed(call)
  );
  
  if (callsToUpdate.length > 0) {
    const callIds = callsToUpdate.map(call => call.id);
    
    try {
      await supabase
        .from('calls')
        .update({ viewed: true })
        .in('id', callIds);
    } catch (error) {
      console.error('Error updating viewed status:', error);
    }
  }
  
  return calls.map(call => ({
    ...call,
    viewed: shouldBeViewed(call)
  }));
};

const getDisplayName = (call) => {
  if (!call.contact?.name || call.contact.name.toLowerCase().includes('customer')) {
    return formatPhoneNumber(call.from_number);
  }
  return call.contact.name;
};

const CallsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const filterDropdownRef = useRef(null);
  
  // Help panel hook
  const helpPanel = useHelpPanel({
    title: "Understanding Activity Management",
    sections: helpPanelSections,
    position: "right",
    size: "large"
  });

  const [calls, setCalls] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [allItems, setAllItems] = useState([]); // Combined calls and conversations
  const [displayItems, setDisplayItems] = useState([]); // Items to display after filtering
  const [filteredItems, setFilteredItems] = useState([]); // Items after filtering but before pagination
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    viewed: 'all', // Default to showing all calls
    type: 'all', // New filter for call vs text
    channel: 'all', // Channel filter for SMS/Email/Webchat
    ai_activity: 'all' // AI tool activity filter
  });

  // Split view state for desktop
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedItemLoading, setSelectedItemLoading] = useState(false);

  // Removed mobile menu state - now handled by TopNav

  // Infinite scroll state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [totalCalls, setTotalCalls] = useState(0);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);
  
  // Intersection observer ref for infinite scroll
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    item: null
  });
  const contextMenuRef = useRef(null);
  
  // Touch/hold state for mobile
  const [touchHold, setTouchHold] = useState({
    timer: null,
    isHolding: false,
    item: null
  });

  // Function to update conversation tasks in local state
  const updateConversationTasks = useCallback((conversationId, updatedTasks) => {
    setAllItems(prev => prev.map(item => {
      if (item.type === 'conversation' && item.id === conversationId) {
        return { ...item, todo_items: updatedTasks };
      }
      return item;
    }));
  }, []);

  // Handle search and filter changes
  useEffect(() => {
    filterAndPaginateItems(allItems, searchQuery, filters.status, filters.viewed, filters.type);
  }, [allItems, searchQuery, filters]);

  // Load initial data on mount
  useEffect(() => {
    handleRefresh();
  }, []);

  // Real-time subscriptions - auto-refresh when mixed messages API completes
  useEffect(() => {
    if (!supabase) return;

    let subscriptions = [];

    const setupLiveUpdates = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Monitor key tables for changes (mixed messages API updates these)
      const tables = [
        'calls', 'chat_conversations', 'chat_messages', 'todo_items', 'conversation_threads'
      ];

      tables.forEach(tableName => {
        const subscription = supabase
          .channel(`${tableName}-updates`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public', 
            table: tableName
          }, () => {
    
            // Debounced refresh for smooth UX
            setTimeout(() => handleRefresh(), 1000);
          })
          .subscribe();

        subscriptions.push(subscription);
      });
    };

    setupLiveUpdates();

    return () => subscriptions.forEach(sub => sub.unsubscribe());
  }, [supabase]);

  // Debug function to check for orphaned texts (can be removed later)
  const debugOrphanedTexts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get user's locations
      const { data: userLocations } = await supabase
        .from('vapi_location_settings')
        .select('id')
        .eq('account_id', user.id);
      
      const locationIds = userLocations?.map(loc => loc.id) || [];
      
      if (locationIds.length > 0) {
        try {
          // Check for texts without valid thread_id
          const { data: orphanedTexts, error: textsError } = await supabase
            .from('texts')
            .select('id, thread_id, content, created_at')
            .not('thread_id', 'is', null)
            .limit(10);
          
          if (textsError) {
            console.warn('Error fetching texts for debug:', textsError);
          } else {
            console.log('Sample texts:', orphanedTexts);
          }
        } catch (error) {
          console.warn('Failed to fetch texts for debug:', error);
        }
        
        try {
          // Check for conversation threads without messages
          const { data: emptyThreads, error: threadsError } = await supabase
            .from('conversation_threads')
            .select('id, created_at, updated_at, config')
            .in('location_id', locationIds)
            .limit(10);
          
          if (threadsError) {
            console.warn('Error fetching threads for debug:', threadsError);
          } else {
            console.log('Sample conversation threads:', emptyThreads);
          }
        } catch (error) {
          console.warn('Failed to fetch threads for debug:', error);
        }
      }
    } catch (error) {
      console.error('Debug error:', error);
    }
  };

  // Uncomment this line temporarily to debug the issue
  // useEffect(() => { debugOrphanedTexts(); }, []);

  // Update URL when page changes
  const updatePage = (newPage) => {
    setCurrentPage(newPage);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newPage === 1) {
      newSearchParams.delete('page');
    } else {
      newSearchParams.set('page', newPage.toString());
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Close dropdown when clicking outside
  useClickAway(filterDropdownRef, () => {
    if (showFilterDropdown) setShowFilterDropdown(false);
  });

  // Close context menu when clicking outside
  useClickAway(contextMenuRef, () => {
    if (contextMenu.visible) setContextMenu(prev => ({ ...prev, visible: false }));
  });

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreData && !isLoadingMore && !isInitialLoading && displayItems.length > 0) {
          loadMoreItems();
        }
      },
      {
        threshold: 0,
        rootMargin: '50px',
        // Use the scroll container as root for desktop view, null for mobile (viewport)
        root: window.innerWidth >= 768 ? scrollContainerRef.current : null
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMoreData, isLoadingMore, isInitialLoading, displayItems.length, scrollContainerRef.current]);

  // Additional scroll event listener for desktop to ensure infinite scroll works
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth >= 768 && scrollContainerRef.current && hasMoreData && !isLoadingMore && !isInitialLoading) {
        const container = scrollContainerRef.current;
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        
        // Trigger load more when within 100px of bottom
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          loadMoreItems();
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container && window.innerWidth >= 768) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [hasMoreData, isLoadingMore, isInitialLoading, scrollContainerRef.current]);

  // Load more items function - fetch from server
  const loadMoreItems = async () => {
    if (isLoadingMore || !hasMoreData) return;

    try {
      setIsLoadingMore(true);
      setError(null);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');

      // Find the oldest item in current display to use as cursor
      const currentItems = displayItems.filter(item => !item.pinned); // Don't count pinned items
      const oldestItem = currentItems[currentItems.length - 1];
      
      if (!oldestItem) {
        setHasMoreData(false);
        return;
      }

      const oldestDate = oldestItem.sort_date;

      // Get user's locations first for legacy thread filtering
      const { data: userLocations } = await supabase
        .from('vapi_location_settings')
        .select('id')
        .eq('account_id', accountId);
      
      const locationIds = userLocations?.map(loc => loc.id) || [];

      // Fetch next batch of items older than the current oldest item
      const [callsResult, conversationsResult, legacyThreadsResult, todoItemsResult] = await Promise.all([
        // Fetch calls older than the oldest current item
        supabase
          .from('calls')
          .select(`
            id,
            created_at,
            from_number,
            call_category,
            viewed,
            summary,
            pinned,
            contact:contacts(id, name)
          `)
          .eq('account_id', accountId)
          .eq('visible', true)
          .lt('created_at', oldestDate)
          .order('created_at', { ascending: false })
          .limit(itemsPerPage * 2), // Fetch more to ensure we get enough mixed items

        // NEW: Fetch chat conversations older than the oldest current item
        supabase
          .from('chat_conversations')
          .select(`
            id,
            created_at,
            last_activity,
            customer_identifier,
            business_identifier,
            channel,
            status,
            message_count,
            latest_message,
            initial_message,
            metadata,
            account_id
          `)
          .eq('account_id', accountId)
          .in('channel', ['sms', 'email', 'webchat'])
          .lt('last_activity', oldestDate)
          .order('last_activity', { ascending: false })
          .limit(itemsPerPage * 2),

        // LEGACY: Fetch conversation threads older than the oldest current item
        locationIds.length > 0 ? supabase
          .from('conversation_threads')
          .select(`
            id,
            created_at,
            updated_at,
            viewed,
            pinned,
            summary,
            contact:contacts(id, name, phone_number),
            location_id
          `)
          .eq('is_active', true)
          .in('location_id', locationIds)
          .lt('updated_at', oldestDate)
          .order('updated_at', { ascending: false })
          .limit(itemsPerPage * 2) : Promise.resolve({ data: [], error: null }),

        // Fetch todo items for both calls and chat conversations
        supabase
          .from('todo_items')
          .select('call_id, chat_conversation_id, is_completed, task_schema')
          .eq('account_id', accountId)
      ]);

      if (callsResult.error) throw callsResult.error;
      if (conversationsResult.error) throw conversationsResult.error;
      if (legacyThreadsResult.error) throw legacyThreadsResult.error;
      if (todoItemsResult.error) throw todoItemsResult.error;

      // Process todo items into lookup maps for both calls and conversations
      const todoLookup = {};
      const conversationTodoLookup = {};
      (todoItemsResult.data || []).forEach(todo => {
        // Handle call todos
        if (todo.call_id) {
          if (!todoLookup[todo.call_id]) {
            todoLookup[todo.call_id] = { total: 0, completed: 0, urgent: 0 };
          }
          todoLookup[todo.call_id].total++;
          if (todo.is_completed) {
            todoLookup[todo.call_id].completed++;
          }
          if (todo.task_schema?.priority === 'urgent') {
            todoLookup[todo.call_id].urgent++;
          }
        }
        
        // Handle conversation todos
        if (todo.chat_conversation_id) {
          if (!conversationTodoLookup[todo.chat_conversation_id]) {
            conversationTodoLookup[todo.chat_conversation_id] = { total: 0, completed: 0, urgent: 0 };
          }
          conversationTodoLookup[todo.chat_conversation_id].total++;
          if (todo.is_completed) {
            conversationTodoLookup[todo.chat_conversation_id].completed++;
          }
          if (todo.task_schema?.priority === 'urgent') {
            conversationTodoLookup[todo.chat_conversation_id].urgent++;
          }
        }
      });

      // NEW: Get latest customer messages for new conversations
      const newConversationIds = conversationsResult.data?.map(conv => conv.id) || [];
      let messagesLookup = {};
      
      if (newConversationIds.length > 0) {
        const { data: latestMessages } = await supabase
          .from('chat_messages')
          .select(`
            conversation_id,
            sender_type,
            message_content,
            created_at,
            channel,
            metadata
          `)
          .in('conversation_id', newConversationIds)
          .eq('sender_type', 'customer')
          .order('created_at', { ascending: false });

        // Create lookup for latest customer messages
        (latestMessages || []).forEach(msg => {
          if (!messagesLookup[msg.conversation_id] && msg.message_content?.trim()) {
            messagesLookup[msg.conversation_id] = msg;
          }
        });
      }

      // Process new calls
      const newCalls = (callsResult.data || []).map(call => {
        const todoInfo = todoLookup[call.id] || { total: 0, completed: 0 };
        return {
          ...call,
          type: 'call',
          sort_date: call.created_at,
          viewed: call.call_category === 'action_required' 
            ? (todoInfo.total === 0 || todoInfo.completed === todoInfo.total)
            : call.viewed,
          todo_items: Array.from({ length: todoInfo.total }, (_, i) => ({
            id: `${call.id}-${i}`,
            is_completed: i < todoInfo.completed
          }))
        };
      });

      // NEW: Process new chat conversations
      const newConversations = (conversationsResult.data || [])
        .map(conversation => {
          const latestMessage = messagesLookup[conversation.id];
          const todoInfo = conversationTodoLookup[conversation.id] || { total: 0, completed: 0, urgent: 0 };
          
          // Extract display name from customer identifier
          const phoneNumber = conversation.customer_identifier;
          const emailAddress = conversation.customer_identifier;
          let displayName = '';
          let contactInfo = {};
          
          if (conversation.channel === 'sms') {
            displayName = formatPhoneNumber(phoneNumber);
            contactInfo = { name: displayName, phone_number: phoneNumber };
          } else if (conversation.channel === 'email') {
            displayName = emailAddress;
            contactInfo = { name: displayName, email_address: emailAddress };
          } else {
            displayName = conversation.customer_identifier;
            contactInfo = { name: displayName };
          }
          
          // Extract tool call information from metadata
          const hasToolCalls = conversation.metadata?.has_tool_calls || false;
          const toolCallCount = conversation.metadata?.tool_call_count || 0;
          const aiResponseCount = conversation.metadata?.ai_response_count || 0;
          const lastToolCall = conversation.metadata?.last_tool_call || null;
          
          return {
            ...conversation,
            type: 'conversation',
            from_number: conversation.channel === 'sms' ? phoneNumber : null,
            last_message: conversation.latest_message || latestMessage?.message_content,
            sort_date: conversation.last_activity,
            contact: contactInfo,
            
            // NEW: Tool call tracking fields
            has_tool_calls: hasToolCalls,
            tool_call_count: toolCallCount,
            ai_responses: aiResponseCount,
            last_tool_call: lastToolCall,
            
            // NEW: Channel-specific fields
            channel_type: conversation.channel,
            conversation_status: conversation.status,
            
            // NEW: Todo items for action required logic
            todo_items: Array.from({ length: todoInfo.total }, (_, i) => ({
              id: `${conversation.id}-${i}`,
              is_completed: i < todoInfo.completed,
              is_urgent: i < todoInfo.urgent
            })),
            
            // Keep existing fields for compatibility
            viewed: todoInfo.total > 0 
              ? (todoInfo.total === 0 || todoInfo.completed === todoInfo.total)
              : (conversation.metadata?.viewed || false),
            pinned: conversation.metadata?.pinned || false
          };
        })
        .filter(conversation => 
          conversation.message_count > 0 && 
          (conversation.latest_message || conversation.initial_message)
        );

      // LEGACY: Get latest messages for legacy conversation threads
      const legacyThreadIds = legacyThreadsResult.data?.map(thread => thread.id).filter(id => id) || [];
      let legacyMessagesLookup = {};
      
      if (legacyThreadIds.length > 0) {
        try {
          const { data: legacyMessages, error: legacyMessagesError } = await supabase
            .from('texts')
            .select(`
              thread_id,
              content,
              created_at,
              from_number
            `)
            .in('thread_id', legacyThreadIds)
            .order('created_at', { ascending: false });

          if (legacyMessagesError) {
            console.warn('Error fetching legacy messages:', legacyMessagesError);
          } else {
            // Create lookup for latest messages in each thread
            (legacyMessages || []).forEach(msg => {
              if (!legacyMessagesLookup[msg.thread_id] && msg.content?.trim()) {
                legacyMessagesLookup[msg.thread_id] = msg;
              }
            });
          }
        } catch (error) {
          console.warn('Failed to fetch legacy messages:', error);
        }
      }

      // LEGACY: Process legacy conversation threads
      const legacyConversations = (legacyThreadsResult.data || [])
        .map(thread => {
          const latestMessage = legacyMessagesLookup[thread.id];
          
          // Use contact info if available, otherwise fallback to phone number
          const displayName = thread.contact?.name || formatPhoneNumber(thread.contact?.phone_number || '');
          const contactInfo = thread.contact || { name: displayName };
          
          return {
            ...thread,
            type: 'conversation', // Same type as new conversations for UI consistency
            legacy: true, // Flag to identify legacy conversations
            from_number: thread.contact?.phone_number,
            last_message: latestMessage?.content,
            sort_date: thread.updated_at,
            contact: contactInfo,
            
            // Default values for new conversation fields
            has_tool_calls: false,
            tool_call_count: 0,
            ai_responses: 0,
            last_tool_call: null,
            channel_type: 'sms', // Assume SMS for legacy threads
            conversation_status: 'completed', // Assume completed for legacy
            
            // Use existing fields
            viewed: thread.viewed || false,
            pinned: thread.pinned || false
          };
        })
        .filter(thread => 
          legacyMessagesLookup[thread.id] // Only include threads that have messages
        );

      // Combine new items and sort chronologically
      const newItems = [...newCalls, ...newConversations, ...legacyConversations]
        .filter(item => !item.pinned) // Don't include pinned items in pagination
        .sort((a, b) => new Date(b.sort_date) - new Date(a.sort_date))
        .slice(0, itemsPerPage); // Take only the next batch

      // Filter out any duplicates (shouldn't happen but safety check)
      const existingIds = new Set(displayItems.map(item => `${item.type}-${item.id}`));
      const uniqueNewItems = newItems.filter(item => 
        !existingIds.has(`${item.type}-${item.id}`)
      );

      if (uniqueNewItems.length === 0) {
        setHasMoreData(false);
        return;
      }

      // Update state with new items
      const updatedCalls = [...calls, ...newCalls];
      const updatedConversations = [...conversations, ...newConversations, ...legacyConversations];
      const updatedDisplayItems = [...displayItems, ...uniqueNewItems];
      const updatedAllItems = [...allItems, ...uniqueNewItems];

      setCalls(updatedCalls);
      setConversations(updatedConversations);
      setAllItems(updatedAllItems);
      setDisplayItems(updatedDisplayItems);
      setFilteredItems(updatedDisplayItems);
      setTotalCalls(updatedDisplayItems.length);

      // Check if there might be more data
      setHasMoreData(uniqueNewItems.length === itemsPerPage);

    } catch (error) {
      console.error('Error loading more items:', error);
      setError('Failed to load more items. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Auto-select first item when data loads (desktop only)
  useEffect(() => {
    if (!isInitialLoading && displayItems.length > 0 && !selectedItem && window.innerWidth >= 768) {
      const firstItem = displayItems[0];
      setSelectedItem(firstItem);
      setSelectedItemType(firstItem.type);
    }
  }, [displayItems, isInitialLoading, selectedItem]);

  // Cleanup touch timers on unmount
  useEffect(() => {
    return () => {
      if (touchHold.timer) {
        clearTimeout(touchHold.timer);
      }
    };
  }, [touchHold.timer]);

  // Prevent page-level scrolling and mobile zoom
  useEffect(() => {
    // Store original styles
    const originalBodyStyle = document.body.style.cssText;
    const originalHtmlStyle = document.documentElement.style.cssText;
    
    // Apply fixed styles
    document.body.style.cssText = `
      overflow: hidden;
      height: 100vh;
      margin: 0;
      padding: 0;
      touch-action: manipulation;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
      overscroll-behavior: none;
      -webkit-overflow-scrolling: touch;
    `;
    
    document.documentElement.style.cssText = `
      overflow: hidden;
      height: 100vh;
      margin: 0;
      padding: 0;
      touch-action: manipulation;
      overscroll-behavior: none;
    `;
    
    // Cleanup on unmount
    return () => {
      document.body.style.cssText = originalBodyStyle;
      document.documentElement.style.cssText = originalHtmlStyle;
    };
  }, []);

  const handleItemClick = async (item) => {
    // Set the selected item ID for animation
    setSelectedCallId(item.id);
    
    // Check if we're on mobile (screen width < 768px)
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      // Mobile behavior: navigate to details page
      if (item.type === 'call') {
        // For non-action required calls, mark as viewed when clicked
        if (item.call_category !== 'action_required' && !item.viewed) {
          try {
            await supabase
              .from('calls')
              .update({ viewed: true })
              .eq('id', item.id);
            
            // Update local state
            setCalls(prevCalls => 
              prevCalls.map(call => 
                call.id === item.id ? { ...call, viewed: true } : call
              )
            );
            setAllItems(prevItems => 
              prevItems.map(it => 
                it.id === item.id && it.type === 'call' ? { ...it, viewed: true } : it
              )
            );
          } catch (error) {
            console.error('Error updating viewed status:', error);
          }
        }
        
        // Navigate to call details
        setTimeout(() => {
          navigate(`/monitor/calls/${item.id}`);
        }, 350);
      } else {
        // NEW: For chat conversations, mark as viewed when clicked (if not already viewed)
        if (!item.viewed) {
          try {
            if (item.legacy) {
              // Update legacy conversation thread
            await supabase
              .from('conversation_threads')
              .update({ viewed: true })
              .eq('id', item.id);
            } else {
              // Update new chat conversation
              await supabase
                .from('chat_conversations')
                .update({ 
                  metadata: { 
                    ...item.metadata, 
                    viewed: true 
                  } 
                })
                .eq('id', item.id);
            }
            
            // Update local state
            setConversations(prevConversations => 
              prevConversations.map(conv => 
                conv.id === item.id ? { ...conv, viewed: true } : conv
              )
            );
            setAllItems(prevItems => 
              prevItems.map(it => 
                it.id === item.id && it.type === 'conversation' ? { ...it, viewed: true } : it
              )
            );
          } catch (error) {
            console.error('Error updating conversation viewed status:', error);
          }
        }
        
        // Navigate to appropriate details page
        setTimeout(() => {
          if (item.legacy) {
            // Navigate to legacy text details page
          navigate(`/monitor/texts/${item.id}`);
          } else {
            // Navigate to new chat conversation details
            navigate(`/monitor/chats/${item.id}`);
          }
        }, 350);
      }
    } else {
      // Desktop behavior: show in split view
      if (item.type === 'call') {
        // For non-action required calls, mark as viewed when clicked
        if (item.call_category !== 'action_required' && !item.viewed) {
          try {
            await supabase
              .from('calls')
              .update({ viewed: true })
              .eq('id', item.id);
            
            // Update local state
            setCalls(prevCalls => 
              prevCalls.map(call => 
                call.id === item.id ? { ...call, viewed: true } : call
              )
            );
            setAllItems(prevItems => 
              prevItems.map(it => 
                it.id === item.id && it.type === 'call' ? { ...it, viewed: true } : it
              )
            );
          } catch (error) {
            console.error('Error updating viewed status:', error);
          }
        }
      } else {
        // NEW: For chat conversations, mark as viewed when clicked (if not already viewed)
        if (!item.viewed) {
          try {
            if (item.legacy) {
              // Update legacy conversation thread
            await supabase
              .from('conversation_threads')
              .update({ viewed: true })
              .eq('id', item.id);
            } else {
              // Update new chat conversation
              await supabase
                .from('chat_conversations')
                .update({ 
                  metadata: { 
                    ...item.metadata, 
                    viewed: true 
                  } 
                })
                .eq('id', item.id);
            }
            
            // Update local state
            setConversations(prevConversations => 
              prevConversations.map(conv => 
                conv.id === item.id ? { ...conv, viewed: true } : conv
              )
            );
            setAllItems(prevItems => 
              prevItems.map(it => 
                it.id === item.id && it.type === 'conversation' ? { ...it, viewed: true } : it
              )
            );
          } catch (error) {
            console.error('Error updating conversation viewed status:', error);
          }
        }
      }
      
      // Set loading state and selected item for split view
      setSelectedItemLoading(true);
      setSelectedItem(item);
      setSelectedItemType(item.type);
      
      // Clear loading state after a brief delay to allow the component to mount
      setTimeout(() => {
        setSelectedItemLoading(false);
      }, 100);
    }
  };

  // Filter items for infinite scroll
  const filterAndPaginateItems = (items, query, statusFilter, viewedFilter, typeFilter) => {
    // Separate pinned and non-pinned items
    const pinnedItems = items.filter(item => item.pinned);
    const nonPinnedItems = items.filter(item => !item.pinned);
    
    let filtered = [...nonPinnedItems];

    // Apply search filter - ENHANCED VERSION
    if (query) {
      const searchLower = query.toLowerCase();
      filtered = filtered.filter(item => {
        const contactName = item.contact?.name?.toLowerCase() || '';
        const fromNumber = item.from_number?.toLowerCase() || '';
        const summary = item.summary?.toLowerCase() || '';
        const lastMessage = item.last_message?.toLowerCase() || '';
        const initialMessage = item.initial_message?.toLowerCase() || '';
        
        // NEW: Search in channel and tool call data
        const channelType = item.channel_type?.toLowerCase() || '';
        const toolCallSummary = item.last_tool_call?.name?.toLowerCase() || '';
        const customerIdentifier = item.customer_identifier?.toLowerCase() || '';
        
        return contactName.includes(searchLower) || 
               fromNumber.includes(searchLower) || 
               summary.includes(searchLower) ||
               lastMessage.includes(searchLower) ||
               initialMessage.includes(searchLower) ||
               channelType.includes(searchLower) ||
               toolCallSummary.includes(searchLower) ||
               customerIdentifier.includes(searchLower);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'action_required') {
        filtered = filtered.filter(item => 
          item.type === 'call' && item.call_category === 'action_required'
        );
      } else if (statusFilter === 'completed') {
        filtered = filtered.filter(item => 
          item.type === 'call' && 
          item.call_category === 'action_required' && 
          item.todo_items && 
          item.todo_items.length > 0 && 
          item.todo_items.every(task => task.is_completed)
        );
      } else if (statusFilter === 'uncompleted') {
        filtered = filtered.filter(item => 
          item.type === 'call' && 
          item.call_category === 'action_required' && 
          item.todo_items && 
          item.todo_items.length > 0 && 
          item.todo_items.some(task => !task.is_completed)
        );
      }
    }

    // Apply viewed filter
    if (viewedFilter !== 'all') {
      if (viewedFilter === 'viewed') {
        filtered = filtered.filter(item => item.viewed);
      } else if (viewedFilter === 'unviewed') {
        filtered = filtered.filter(item => !item.viewed);
      }
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }



    // Store filtered items for infinite scroll
    setFilteredItems(filtered);
    
    // For infinite scroll, show all filtered items (pinned + filtered)
    const allDisplayItems = [...pinnedItems, ...filtered];
    setDisplayItems(allDisplayItems);
    // Don't override hasMoreData here - it should be managed by loadMoreItems based on server response
    setTotalCalls(filtered.length);
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      setError(null);
      
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');

      // Fetch a larger pool to ensure we get the 25 most recent items overall
      const fetchLimit = itemsPerPage * 3; // Fetch 75 of each to ensure we get the top 25 combined

      // Get user's locations first for legacy thread filtering
      const { data: userLocations } = await supabase
        .from('vapi_location_settings')
        .select('id')
        .eq('account_id', accountId);
      
      const locationIds = userLocations?.map(loc => loc.id) || [];

      // Run queries in PARALLEL for maximum speed
      const [callsResult, conversationsResult, legacyThreadsResult, todoItemsResult] = await Promise.all([
        // Fetch more calls to ensure proper chronological mixing
        supabase
          .from('calls')
          .select(`
            id,
            created_at,
            from_number,
            call_category,
            viewed,
            summary,
            pinned,
            contact:contacts(id, name)
          `)
          .eq('account_id', accountId)
          .eq('visible', true)
          .order('created_at', { ascending: false })
          .limit(fetchLimit),

        // NEW: Fetch both new chat conversations and legacy conversation threads
        supabase
          .from('chat_conversations')
          .select(`
            id,
            created_at,
            last_activity,
            customer_identifier,
            business_identifier,
            channel,
            status,
            message_count,
            latest_message,
            initial_message,
            metadata,
            account_id
          `)
          .eq('account_id', accountId)
          .in('channel', ['sms', 'email', 'webchat'])
          .order('last_activity', { ascending: false })
          .limit(fetchLimit),

        // LEGACY: Fetch conversation threads for backward compatibility
        locationIds.length > 0 ? supabase
          .from('conversation_threads')
          .select(`
            id,
            created_at,
            updated_at,
            viewed,
            pinned,
            summary,
            contact:contacts(id, name, phone_number),
            location_id
          `)
          .eq('is_active', true)
          .in('location_id', locationIds)
          .order('updated_at', { ascending: false })
          .limit(fetchLimit) : Promise.resolve({ data: [], error: null }),

        // Fetch all todo items for both calls and chat conversations
        supabase
          .from('todo_items')
          .select('call_id, chat_conversation_id, is_completed, task_schema')
          .eq('account_id', accountId)
      ]);

      if (callsResult.error) throw callsResult.error;
      if (conversationsResult.error) throw conversationsResult.error;
      if (legacyThreadsResult.error) throw legacyThreadsResult.error;
      if (todoItemsResult.error) throw todoItemsResult.error;

      // Process todo items into lookup maps for both calls and conversations
      const todoLookup = {};
      const conversationTodoLookup = {};
      (todoItemsResult.data || []).forEach(todo => {
        // Handle call todos
        if (todo.call_id) {
          if (!todoLookup[todo.call_id]) {
            todoLookup[todo.call_id] = { total: 0, completed: 0, urgent: 0 };
          }
          todoLookup[todo.call_id].total++;
          if (todo.is_completed) {
            todoLookup[todo.call_id].completed++;
          }
          if (todo.task_schema?.priority === 'urgent') {
            todoLookup[todo.call_id].urgent++;
          }
        }
        
        // Handle conversation todos
        if (todo.chat_conversation_id) {
          if (!conversationTodoLookup[todo.chat_conversation_id]) {
            conversationTodoLookup[todo.chat_conversation_id] = { total: 0, completed: 0, urgent: 0 };
          }
          conversationTodoLookup[todo.chat_conversation_id].total++;
          if (todo.is_completed) {
            conversationTodoLookup[todo.chat_conversation_id].completed++;
          }
          if (todo.task_schema?.priority === 'urgent') {
            conversationTodoLookup[todo.chat_conversation_id].urgent++;
          }
        }
      });

      // NEW: Get latest customer messages for conversations
      const conversationIds = conversationsResult.data?.map(conv => conv.id) || [];
      let messagesLookup = {};
      
      if (conversationIds.length > 0) {
        const { data: latestMessages } = await supabase
          .from('chat_messages')
          .select(`
            conversation_id,
            sender_type,
            message_content,
            created_at,
            channel,
            metadata
          `)
          .in('conversation_id', conversationIds)
          .eq('sender_type', 'customer')
          .order('created_at', { ascending: false });

        // Create lookup for latest customer messages
        (latestMessages || []).forEach(msg => {
          if (!messagesLookup[msg.conversation_id] && msg.message_content?.trim()) {
            messagesLookup[msg.conversation_id] = msg;
          }
        });
      }

      // Process calls
      const processedCalls = (callsResult.data || []).map(call => {
        const todoInfo = todoLookup[call.id] || { total: 0, completed: 0 };
        return {
          ...call,
          type: 'call',
          sort_date: call.created_at,
          viewed: call.call_category === 'action_required' 
            ? (todoInfo.total === 0 || todoInfo.completed === todoInfo.total)
            : call.viewed,
          todo_items: Array.from({ length: todoInfo.total }, (_, i) => ({
            id: `${call.id}-${i}`,
            is_completed: i < todoInfo.completed
          }))
        };
      });

      // NEW: Process chat conversations
      const processedConversations = (conversationsResult.data || [])
        .map(conversation => {
          const latestMessage = messagesLookup[conversation.id];
          const todoInfo = conversationTodoLookup[conversation.id] || { total: 0, completed: 0, urgent: 0 };
          
          // Extract display name from customer identifier
          const phoneNumber = conversation.customer_identifier;
          const emailAddress = conversation.customer_identifier;
          let displayName = '';
          let contactInfo = {};
          
          if (conversation.channel === 'sms') {
            displayName = formatPhoneNumber(phoneNumber);
            contactInfo = { name: displayName, phone_number: phoneNumber };
          } else if (conversation.channel === 'email') {
            displayName = emailAddress;
            contactInfo = { name: displayName, email_address: emailAddress };
          } else {
            displayName = conversation.customer_identifier;
            contactInfo = { name: displayName };
          }
          
          // Extract tool call information from metadata
          const hasToolCalls = conversation.metadata?.has_tool_calls || false;
          const toolCallCount = conversation.metadata?.tool_call_count || 0;
          const aiResponseCount = conversation.metadata?.ai_response_count || 0;
          const lastToolCall = conversation.metadata?.last_tool_call || null;
          
          return {
            ...conversation,
            type: 'conversation',
            from_number: conversation.channel === 'sms' ? phoneNumber : null,
            last_message: conversation.latest_message || latestMessage?.message_content,
            sort_date: conversation.last_activity,
            contact: contactInfo,
            
            // NEW: Tool call tracking fields
            has_tool_calls: hasToolCalls,
            tool_call_count: toolCallCount,
            ai_responses: aiResponseCount,
            last_tool_call: lastToolCall,
            
            // NEW: Channel-specific fields
            channel_type: conversation.channel,
            conversation_status: conversation.status,
            
            // NEW: Todo items for action required logic
            todo_items: Array.from({ length: todoInfo.total }, (_, i) => ({
              id: `${conversation.id}-${i}`,
              is_completed: i < todoInfo.completed,
              is_urgent: i < todoInfo.urgent
            })),
            
            // Keep existing fields for compatibility
            viewed: todoInfo.total > 0 
              ? (todoInfo.total === 0 || todoInfo.completed === todoInfo.total)
              : (conversation.metadata?.viewed || false),
            pinned: conversation.metadata?.pinned || false
          };
        })
        .filter(conversation => 
          conversation.message_count > 0 && 
          (conversation.latest_message || conversation.initial_message)
        );

      // LEGACY: Get latest messages for legacy conversation threads
      const legacyThreadIds = legacyThreadsResult.data?.map(thread => thread.id).filter(id => id) || [];
      let legacyMessagesLookup = {};
      
      if (legacyThreadIds.length > 0) {
        try {
          const { data: legacyMessages, error: legacyMessagesError } = await supabase
            .from('texts')
            .select(`
              thread_id,
              content,
              created_at,
              from_number
            `)
            .in('thread_id', legacyThreadIds)
            .order('created_at', { ascending: false });

          if (legacyMessagesError) {
            console.warn('Error fetching legacy messages:', legacyMessagesError);
          } else {
            // Create lookup for latest messages in each thread
            (legacyMessages || []).forEach(msg => {
              if (!legacyMessagesLookup[msg.thread_id] && msg.content?.trim()) {
                legacyMessagesLookup[msg.thread_id] = msg;
              }
            });
          }
        } catch (error) {
          console.warn('Failed to fetch legacy messages:', error);
        }
      }

      // LEGACY: Process legacy conversation threads
      const processedLegacyConversations = (legacyThreadsResult.data || [])
        .map(thread => {
          const latestMessage = legacyMessagesLookup[thread.id];
          
          // Use contact info if available, otherwise fallback to phone number
          const displayName = thread.contact?.name || formatPhoneNumber(thread.contact?.phone_number || '');
          const contactInfo = thread.contact || { name: displayName };
          
          return {
            ...thread,
            type: 'conversation', // Same type as new conversations for UI consistency
            legacy: true, // Flag to identify legacy conversations
            from_number: thread.contact?.phone_number,
            last_message: latestMessage?.content,
            sort_date: thread.updated_at,
            contact: contactInfo,
            
            // Default values for new conversation fields
            has_tool_calls: false,
            tool_call_count: 0,
            ai_responses: 0,
            last_tool_call: null,
            channel_type: 'sms', // Assume SMS for legacy threads
            conversation_status: 'completed', // Assume completed for legacy
            
            // Use existing fields
            viewed: thread.viewed || false,
            pinned: thread.pinned || false
          };
        })
        .filter(thread => 
          legacyMessagesLookup[thread.id] // Only include threads that have messages
        );

      // Combine ALL items and sort chronologically
      const allCombinedItems = [...processedCalls, ...processedConversations, ...processedLegacyConversations]
        .sort((a, b) => new Date(b.sort_date) - new Date(a.sort_date));

      // Separate pinned items and take only the top 25 non-pinned items
      const pinnedItems = allCombinedItems.filter(item => item.pinned);
      const nonPinnedItems = allCombinedItems.filter(item => !item.pinned);
      const topRecentItems = nonPinnedItems.slice(0, itemsPerPage);

      // Final combined list: pinned first, then top 25 most recent
      const finalCombinedItems = [...pinnedItems, ...topRecentItems]
        .sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          return new Date(b.sort_date) - new Date(a.sort_date);
        });

      // Store the full lists for reference but only show the top items
      setCalls(processedCalls);
      setConversations([...processedConversations, ...processedLegacyConversations]);
      setAllItems(finalCombinedItems);
      setDisplayItems(finalCombinedItems);
      setFilteredItems(finalCombinedItems);
      
      // Reset pagination state
      setCurrentOffset(0);
      setTotalCalls(finalCombinedItems.length);
      
      // Check if there might be more data (if we got a full batch, there's probably more)
      setHasMoreData(nonPinnedItems.length > itemsPerPage);
      
      setIsInitialLoading(false);
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data. Please try again.');
      setIsInitialLoading(false);
    } finally {
      setIsRefreshing(false);
    }
  };

  const totalPages = Math.ceil(totalCalls / itemsPerPage);

  const getCallStatusTag = (call) => {
    if (!call.call_category) return null;
    
    const config = statusConfig[call.call_category];
    if (!config) return null;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${config.color} ${config.bgColor}`}>
        {config.label}
      </span>
    );
  };

  const getConversationStatusTag = (conversation) => {
    const status = getConversationStatus(conversation);
    const config = conversationStatusConfig[status];
    if (!config) return null;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${config.color} ${config.bgColor}`}>
        {config.label}
      </span>
    );
  };

  // Create filter button
  const filterButton = (
    <motion.button
      variants={buttonVariants}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-gray-200 text-gray-700 text-sm font-medium shadow-sm hover:shadow transition-all duration-200"
      aria-label="Filter items"
      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
      aria-expanded={showFilterDropdown}
    >
                      <Funnel className="h-3.5 w-3.5" />
      <span>
        {filters.type === 'call' ? 'Calls Only' : 
         filters.type === 'conversation' ? 'Texts Only' :
         filters.viewed === 'unviewed' ? 'Unviewed Only' : 
         filters.viewed === 'uncompleted' ? 'Uncompleted Only' : 'All Items'}
      </span>
    </motion.button>
  );

  // Find position of filter button to place dropdown correctly
  const desktopFilterButtonRef = useRef(null);
  const mobileFilterButtonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown position when showing
  useEffect(() => {
    if (showFilterDropdown) {
      const isMobile = window.innerWidth < 768;
      const buttonRef = isMobile ? mobileFilterButtonRef.current : desktopFilterButtonRef.current;
      
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        const isSmallMobile = window.innerWidth < 640;
        
        setDropdownPosition({
          top: rect.bottom + 8,
          left: isSmallMobile ? null : rect.left,
          right: isSmallMobile ? 16 : null
        });
      }
    }
  }, [showFilterDropdown]);

  // Function to check if all tasks are completed for action required items
  const areAllTasksCompleted = (item) => {
    if (!item.todo_items || !Array.isArray(item.todo_items) || item.todo_items.length === 0) {
      return true; // No tasks means "completed"
    }
    return item.todo_items.every(task => task.is_completed);
  };

  // Context menu handlers
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      item
    });
  };

  const handleContextAction = async (action, item) => {
    setContextMenu(prev => ({ ...prev, visible: false }));
    
    try {
      switch (action) {
        case 'pin':
        case 'unpin':
          const pinned = action === 'pin';
          
          // Update database first
          if (item.type === 'call') {
            await supabase
              .from('calls')
              .update({ pinned })
              .eq('id', item.id);
          } else if (item.legacy) {
            // Update legacy conversation thread
            await supabase
          .from('conversation_threads')
              .update({ pinned })
              .eq('id', item.id);
          } else {
            // NEW: Update chat conversations metadata
            await supabase
              .from('chat_conversations')
              .update({ 
                metadata: { 
                  ...item.metadata, 
                  pinned 
                } 
              })
              .eq('id', item.id);
          }
          
          // Update local state with proper re-sorting
          if (item.type === 'call') {
            setCalls(prevCalls => {
              const updatedCalls = prevCalls.map(call => 
                call.id === item.id ? { ...call, pinned } : call
              );
              return updatedCalls;
            });
          } else {
            setConversations(prevConversations => {
              const updatedConversations = prevConversations.map(conv => 
                conv.id === item.id ? { ...conv, pinned } : conv
              );
              return updatedConversations;
            });
          }
          
          // Update allItems and displayItems with proper sorting
          setAllItems(prevItems => {
            const updatedItems = prevItems.map(it => 
              it.id === item.id && it.type === item.type ? { ...it, pinned } : it
            );
            
            // Re-sort items: pinned first, then by sort_date
            const sortedItems = updatedItems.sort((a, b) => {
              // Pinned items always come first
              if (a.pinned && !b.pinned) return -1;
              if (!a.pinned && b.pinned) return 1;
              // Within the same pinned status, sort by date (newest first)
              return new Date(b.sort_date) - new Date(a.sort_date);
            });
            
            return sortedItems;
          });
          
          // Also update displayItems to trigger re-render with new positions
          setDisplayItems(prevDisplayItems => {
            const updatedDisplayItems = prevDisplayItems.map(it => 
              it.id === item.id && it.type === item.type ? { ...it, pinned } : it
            );
            
            // Re-sort displayItems the same way
            const sortedDisplayItems = updatedDisplayItems.sort((a, b) => {
              if (a.pinned && !b.pinned) return -1;
              if (!a.pinned && b.pinned) return 1;
              return new Date(b.sort_date) - new Date(a.sort_date);
            });
            
            return sortedDisplayItems;
          });
          
          break;
          
        case 'mark_viewed':
        case 'mark_unviewed':
          const viewed = action === 'mark_viewed';
          if (item.type === 'call') {
            await supabase
              .from('calls')
              .update({ viewed })
              .eq('id', item.id);
            
            setCalls(prevCalls => 
              prevCalls.map(call => 
                call.id === item.id ? { ...call, viewed } : call
              )
            );
          } else if (item.legacy) {
            // Update legacy conversation thread
            await supabase
              .from('conversation_threads')
              .update({ viewed })
              .eq('id', item.id);
            
            setConversations(prevConversations => 
              prevConversations.map(conv => 
                conv.id === item.id ? { ...conv, viewed } : conv
              )
            );
          } else {
            // NEW: Update chat conversations metadata
            await supabase
              .from('chat_conversations')
              .update({ 
                metadata: { 
                  ...item.metadata, 
                  viewed 
                } 
              })
              .eq('id', item.id);
            
            setConversations(prevConversations => 
              prevConversations.map(conv => 
                conv.id === item.id ? { ...conv, viewed } : conv
              )
            );
          }
          
          setAllItems(prevItems => 
            prevItems.map(it => 
              it.id === item.id && it.type === item.type ? { ...it, viewed } : it
            )
          );
          
          // Update displayItems as well
          setDisplayItems(prevDisplayItems => 
            prevDisplayItems.map(it => 
              it.id === item.id && it.type === item.type ? { ...it, viewed } : it
            )
          );
          break;
          
        case 'view':
          handleItemClick(item);
          break;
      }
    } catch (error) {
      console.error('Error performing context action:', error);
      setError('Failed to perform action. Please try again.');
    }
  };

  // Touch handlers for mobile context menu
  const handleTouchStart = (e, item) => {
    const timer = setTimeout(() => {
      setTouchHold({ timer: null, isHolding: true, item });
      // Trigger haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Show context menu
      const touch = e.touches[0];
      handleContextMenu({ 
        preventDefault: () => {}, 
        clientX: touch.clientX, 
        clientY: touch.clientY 
      }, item);
    }, 500);
    
    setTouchHold({ timer, isHolding: false, item });
  };

  const handleTouchEnd = () => {
    if (touchHold.timer) {
      clearTimeout(touchHold.timer);
    }
    setTouchHold({ timer: null, isHolding: false, item: null });
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #e5e7eb;
            border-radius: 3px;
            transition: background-color 0.2s ease;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #d1d5db;
          }
          
          /* For Firefox */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #e5e7eb transparent;
          }
        `
      }} />
      <div 
        className="h-full bg-gray-50 overflow-hidden"
        style={{
          margin: 0,
          padding: 0,
          touchAction: 'manipulation',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none'
        }}
      >
      <div className="h-full flex flex-col">
        {/* Filter Dropdown Menu */}
        <div className="fixed inset-0 z-40" 
          ref={filterDropdownRef} 
          style={{ pointerEvents: showFilterDropdown ? 'auto' : 'none' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowFilterDropdown(false);
          }}
        >
          <AnimatePresence>
            {showFilterDropdown && (
    <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="absolute w-48 bg-white rounded-xl border border-gray-200 py-1 z-50 shadow-lg overflow-hidden"
                style={{ 
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  right: dropdownPosition.right
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type Filter</h3>
                </div>
                {Object.entries({
                  all: 'All Items',
                  call: 'Calls Only',
                  conversation: 'Texts Only'
                }).map(([value, label]) => (
                  <motion.button
                    key={value}
                    onClick={() => {
                      setFilters(prev => ({ ...prev, type: value }));
                      setShowFilterDropdown(false);
                    }}
                    className={`flex items-center w-full text-left px-4 py-2 text-sm ${
                      filters.type === value 
                        ? 'text-blue-600 bg-blue-50' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    transition={{ duration: 0.2 }}
                  >
                    {filters.type === value ? (
                      <Check className="w-4 h-4 mr-2 text-blue-600" />
                    ) : (
                      <div className="w-4 h-4 mr-2" />
                    )}
                    <span>{label}</span>
                  </motion.button>
                ))}
                

                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 border-t">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">View Filter</h3>
                </div>
                {Object.entries({
                  all: 'All Items',
                  unviewed: 'Unviewed Only',
                  uncompleted: 'Uncompleted Only'
                }).map(([value, label]) => (
                  <motion.button
                    key={value}
                    onClick={() => {
                      setFilters(prev => ({ ...prev, viewed: value }));
                      setShowFilterDropdown(false);
                    }}
                    className={`flex items-center w-full text-left px-4 py-2 text-sm ${
                      filters.viewed === value 
                        ? 'text-blue-600 bg-blue-50' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    transition={{ duration: 0.2 }}
                  >
                    {filters.viewed === value ? (
                      <Check className="w-4 h-4 mr-2 text-blue-600" />
                    ) : (
                      <div className="w-4 h-4 mr-2" />
                    )}
                    <span>{label}</span>
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Split View Layout for Desktop */}
        <div className="hidden md:flex h-full">
          {/* Left Panel - Activity List */}
          <div className="w-2/5 flex flex-col bg-white border-r border-gray-200">
            <div className="p-0 flex flex-col h-full">
              {/* List Panel Header with Search and Filter */}
              <div className="p-4 sm:p-5 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                  {/* Search and Filter Row */}
                  <div className="flex items-center gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1">
                      <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Filter Button */}
                    <div>
                      <motion.button
                        ref={desktopFilterButtonRef}
                        variants={buttonVariants}
                        initial="rest"
                        whileHover="hover"
                        whileTap="tap"
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-medium shadow-sm hover:shadow transition-all duration-200"
                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      >
                        <Funnel className="h-4 w-4" />
                        <span>
                          {filters.type === 'call' ? 'Calls' : 
                           filters.type === 'conversation' ? 'Texts' :
                           filters.viewed === 'unviewed' ? 'Unviewed' : 
                           filters.viewed === 'uncompleted' ? 'Uncompleted' : 'All'}
                        </span>
                      </motion.button>
                    </div>
                  </div>
                  
                  {/* Error Display */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl"
                      >
                        <div className="flex items-center text-red-600 text-sm">
                          <WarningCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                          {error}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-1 min-h-0 relative">
                  {isInitialLoading ? (
                    <div className="divide-y divide-gray-50">
                      {Array.from({ length: 15 }).map((_, i) => (
                        <SkeletonItem key={`skeleton-initial-${i}`} />
                      ))}
                    </div>
                  ) : displayItems.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
                        <Newspaper className="w-6 h-6 text-gray-300" />
                      </div>
                      <h3 className="text-gray-400 text-sm font-light mb-2">
                        {searchQuery ? 'No communications match your search' : 'No communications yet'}
                      </h3>
                      <p className="text-gray-400 text-xs">
                        {searchQuery ? 'Try adjusting your filters' : 'Your calls and messages will appear here'}
                      </p>
                    </div>
                  ) : (
                    <div 
                      ref={scrollContainerRef}
                      className="h-full overflow-y-auto overscroll-contain custom-scrollbar"
                      style={{ maxHeight: '100%' }}
                    >
                      <div className="divide-y divide-gray-50">
                        {displayItems.map((item, index) => (
                          <motion.div
                            key={`${item.type}-${item.id}`}
                            layoutId={`${item.type}-${item.id}`}
                            layout
                            variants={listItemVariants}
      initial="initial"
      animate="animate"
                            custom={index}
                            transition={{
                              layout: {
                                duration: 0.4,
                                ease: [0.22, 1, 0.36, 1]
                              },
                              ...listItemVariants.animate.transition
                            }}
                            onClick={() => handleItemClick(item)}
                            onContextMenu={(e) => handleContextMenu(e, item)}
                            className={`py-3 px-4 cursor-pointer transition-colors duration-200 relative ${
                              selectedItem?.id === item.id 
                                ? 'bg-gray-100 border-r-2 border-gray-400' 
                                : (() => {
                                    if (item.type === 'call') {
                                      if (item.call_category === 'action_required') {
                                        return areAllTasksCompleted(item) ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                      }
                                      return item.viewed ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                    } else {
                                      return item.viewed ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                    }
                                  })()
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {/* Dot indicator */}
                              <div className="w-1.5 h-1.5 flex-shrink-0">
                                {((item.type === 'call' && item.call_category === 'action_required') || 
                                  (item.type === 'conversation' && item.todo_items && item.todo_items.length > 0)) && (
                                  <div className={`w-1.5 h-1.5 rounded-full ${
                                    areAllTasksCompleted(item)
                                      ? hasUrgentTasks(item) 
                                        ? 'bg-gray-300'
                                        : 'bg-gray-300'
                                      : hasUrgentTasks(item)
                                        ? 'bg-red-500'
                                        : 'bg-amber-500'
                                  }`} />
                                )}
                              </div>
                              
                              {/* Status indicator */}
                              <div className="relative flex-shrink-0">
                                <div className="w-8 h-8 rounded-full text-gray-400 flex items-center justify-center bg-gray-50">
                                  {item.type === 'call' ? (
                                    <Phone className="w-3.5 h-3.5" />
                                  ) : (
                                    // NEW: Channel-specific icons
                                    item.channel_type === 'sms' ? (
                                    <ChatCircle className="w-3.5 h-3.5" />
                                    ) : item.channel_type === 'email' ? (
                                      <Envelope className="w-3.5 h-3.5" />
                                    ) : (
                                      <Chat className="w-3.5 h-3.5" />
                                    )
                                  )}
                                </div>
                                
                                {/* NEW: Tool call indicator badge */}
                                {item.type === 'conversation' && item.has_tool_calls && (
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                    <span className="text-xs text-white font-bold">{item.tool_call_count}</span>
                                  </div>
                                )}
                                

                              </div>
                              
                              {/* Item information */}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between">
                                  <div className="truncate flex items-center gap-1.5 min-w-0 flex-1">
                                    <span className={`text-sm font-medium truncate ${
                                      (() => {
                                        if (item.type === 'call') {
                                          if (item.call_category === 'action_required') {
                                            return areAllTasksCompleted(item) ? 'text-gray-500' : 'text-gray-900';
                                          }
                                          return item.viewed ? 'text-gray-500' : 'text-gray-900';
                                        } else {
                                          // For conversations, check if they have todo items (action required)
                                          if (item.todo_items && item.todo_items.length > 0) {
                                            return areAllTasksCompleted(item) ? 'text-gray-500' : 'text-gray-900';
                                          }
                                          return item.viewed ? 'text-gray-500' : 'text-gray-900';
                                        }
                                      })()
                                    }`}>
                                      {item.type === 'call' 
                                        ? getDisplayName(item)
                                        : getConversationDisplayName(item)
                                      }
                                    </span>
                                    {item.pinned && (
                                      <PushPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    )}
                                  </div>
                                  
                                  <div className={`flex items-center text-xs ml-2 flex-shrink-0 ${
                                    (() => {
                                      if (item.type === 'call') {
                                        if (item.call_category === 'action_required') {
                                          return areAllTasksCompleted(item) ? 'text-gray-400' : 'text-gray-500';
                                        }
                                        return item.viewed ? 'text-gray-400' : 'text-gray-500';
                                      } else {
                                        return item.viewed ? 'text-gray-400' : 'text-gray-500';
                                      }
                                    })()
                                  }`}>
                                    <span>{formatCallDate(item.sort_date)}</span>
                                  </div>
                                </div>
                                
                                <div className="mt-0.5">
                                  {item.type === 'call' ? (
                                    item?.summary && (
                                      <span className={`text-xs line-clamp-1 ${
                                        (() => {
                                          if (item.call_category === 'action_required') {
                                            return areAllTasksCompleted(item) ? 'text-gray-400' : 'text-gray-500';
                                          }
                                          return item.viewed ? 'text-gray-400' : 'text-gray-500';
                                        })()
                                      }`}>
                                        {item.summary.length > getResponsiveCharLimit() 
                                          ? `${item.summary.substring(0, getResponsiveCharLimit())}...` 
                                          : item.summary}
                                      </span>
                                    )
                                  ) : (
                                    <>
                                      {item?.last_message && (
                                      <span className={`text-xs line-clamp-1 ${
                                        item.viewed ? 'text-gray-400' : 'text-gray-500'
                                      }`}>
                                        {item.last_message.length > getResponsiveCharLimit() 
                                          ? `${item.last_message.substring(0, getResponsiveCharLimit())}...` 
                                          : item.last_message}
                                      </span>
                                      )}
                                      
                                      {/* NEW: Tool call summary below message preview */}
                                      {item.has_tool_calls && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <div className="w-3 h-3 rounded-full bg-blue-100 flex items-center justify-center">
                                            <Lightning className="w-2 h-2 text-blue-600" />
                                </div>
                                          <span className="text-xs text-blue-600 font-medium">
                                            {item.tool_call_count} AI tool{item.tool_call_count !== 1 ? 's' : ''} used
                                          </span>
                                          {item.last_tool_call && (
                                            <span className="text-xs text-gray-400">
                                              • {item.last_tool_call.name}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Loading skeleton items */}
                      {isLoadingMore && (
                        <div className="divide-y divide-gray-50">
                          {Array.from({ length: 3 }).map((_, i) => (
                            <SkeletonItem key={`skeleton-desktop-${i}`} />
                          ))}
                        </div>
                      )}
                      
                      {/* End of list indicator */}
                      {!hasMoreData && displayItems.length > 0 && (
                        <div className="py-4 text-center">
                          <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                            <Check className="w-3 h-3 text-gray-400" />
                          </div>
                          <p className="text-xs text-gray-400">You've reached the end</p>
                        </div>
                      )}
                      
                      {/* Intersection observer target - positioned before the end */}
                      {hasMoreData && (
                        <div 
                          ref={loadMoreRef} 
                          className="h-4 w-full flex items-center justify-center py-1"
                          style={{ minHeight: '16px' }}
                        >
                          <div className="w-1 h-1 bg-gray-200 rounded-full opacity-30" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

          {/* Right Panel - Details View */}
          <div className="w-3/5 flex flex-col h-full bg-white">
            {selectedItemLoading ? (
              <DetailsSkeleton />
            ) : selectedItem ? (
              <div className="h-full flex flex-col min-h-0">
                {selectedItemType === 'call' ? (
                  <div className="h-full flex flex-col min-h-0">
                    <CallDetailsPage id={selectedItem.id} hideBackButton={true} />
                  </div>
                ) : selectedItem.legacy ? (
                  <div className="h-full flex flex-col min-h-0">
                    <TextDetailsPage id={selectedItem.id} hideBackButton={true} />
                  </div>
                ) : (
                  <div className="h-full flex flex-col min-h-0">
                    <ChatConversationDetailsPage conversationId={selectedItem.id} hideBackButton={true} />
                  </div>
                )}
              </div>
            ) : isInitialLoading ? (
              <DetailsSkeleton />
            ) : (
              <div className="bg-white h-full flex items-center justify-center">
                <div className="text-center py-20">
                  <div className="w-12 h-12 mx-auto mb-6 rounded-full bg-gray-50 flex items-center justify-center">
                    <Chat className="w-8 h-8 text-gray-300" />
                  </div>
                  <h3 className="text-gray-400 text-lg font-light mb-2">Select an item to view details</h3>
                  <p className="text-gray-400 text-sm">Click on any call or message to see the full details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile View - Full Height Layout */}
        <div className="md:hidden h-full flex flex-col">
          <div className="bg-white h-full flex flex-col">
            <div className="h-full flex flex-col">
              {/* Mobile Header with Search and Filter */}
              <div className="p-4 sm:p-5 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                {/* Search and Filter Row */}
                <div className="flex items-center gap-3">
                  {/* Search Input */}
                  <div className="relative flex-1">
                    <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Filter Button */}
                  <div>
                    <motion.button
                      ref={mobileFilterButtonRef}
                      variants={buttonVariants}
                      initial="rest"
                      whileHover="hover"
                      whileTap="tap"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-medium shadow-sm hover:shadow transition-all duration-200"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <Funnel className="h-4 w-4" />
                      <span>
                        {filters.type === 'call' ? 'Calls' : 
                         filters.type === 'conversation' ? 'Texts' :
                         filters.viewed === 'unviewed' ? 'Unviewed' : 
                         filters.viewed === 'uncompleted' ? 'Uncompleted' : 'All'}
                      </span>
                    </motion.button>
                  </div>
                </div>
                
                {/* Error Display */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl"
                    >
                      <div className="flex items-center text-red-600 text-sm">
                        <WarningCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        {error}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {isInitialLoading ? (
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                  <div className="divide-y divide-gray-50">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <SkeletonItem key={`skeleton-mobile-initial-${i}`} />
                    ))}
                  </div>
                </div>
              ) : displayItems.length === 0 ? (
                <div className="flex-1 min-h-0 flex items-center justify-center">
                  <div className="text-center py-12">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
                      <Newspaper className="w-6 h-6 text-gray-300" />
                    </div>
                    <h3 className="text-gray-400 text-sm font-light mb-2">
                      {searchQuery ? 'No communications match your search' : 'No communications yet'}
                    </h3>
                    <p className="text-gray-400 text-xs">
                      {searchQuery ? 'Try adjusting your filters' : 'Your calls and messages will appear here'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                  <div className="divide-y divide-gray-50">
                    {displayItems.map((item, index) => (
                      <motion.div
                        key={`${item.type}-${item.id}`}
                        layoutId={`mobile-${item.type}-${item.id}`}
                        layout
                        variants={listItemVariants}
                        initial="initial"
                        animate="animate"
                        custom={index}
                        transition={{
                          layout: {
                            duration: 0.4,
                            ease: [0.22, 1, 0.36, 1]
                          },
                          ...listItemVariants.animate.transition
                        }}
                        onClick={() => handleItemClick(item)}
                        onContextMenu={(e) => handleContextMenu(e, item)}
                        onTouchStart={(e) => handleTouchStart(e, item)}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                        className={`py-4 px-5 cursor-pointer transition-colors duration-200 relative ${
                          selectedCallId === item.id 
                            ? 'bg-blue-50' 
                            : (() => {
                                if (item.type === 'call') {
                                  if (item.call_category === 'action_required') {
                                    return areAllTasksCompleted(item) ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                  }
                                  return item.viewed ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                } else {
                                  // For conversations, check if they have todo items (action required)
                                  if (item.todo_items && item.todo_items.length > 0) {
                                    return areAllTasksCompleted(item) ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                  }
                                  return item.viewed ? 'bg-gray-50/50 hover:bg-gray-50' : 'hover:bg-gray-50';
                                }
                              })()
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Dot indicator */}
                          <div className="w-1.5 h-1.5 flex-shrink-0">
                            {((item.type === 'call' && item.call_category === 'action_required') || 
                              (item.type === 'conversation' && item.todo_items && item.todo_items.length > 0)) && (
                              <div className={`w-1.5 h-1.5 rounded-full ${
                                areAllTasksCompleted(item)
                                  ? hasUrgentTasks(item) 
                                    ? 'bg-gray-300'
                                    : 'bg-gray-300'
                                  : hasUrgentTasks(item)
                                    ? 'bg-red-500'
                                    : 'bg-amber-500'
                              }`} />
                            )}
                          </div>
                          
                          {/* Status indicator */}
                          <div className="relative flex-shrink-0">
                            <div className="w-8 h-8 rounded-full text-gray-400 flex items-center justify-center bg-gray-50">
                              {item.type === 'call' ? (
                                <Phone className="w-3.5 h-3.5" />
                              ) : (
                                // Channel-specific icons (same as desktop)
                                item.channel_type === 'sms' ? (
                                <ChatCircle className="w-3.5 h-3.5" />
                                ) : item.channel_type === 'email' ? (
                                  <Envelope className="w-3.5 h-3.5" />
                                ) : (
                                                                                  <Chat className="w-3.5 h-3.5" />
                                )
                              )}
                            </div>
                            
                            {/* Tool call indicator badge (same as desktop) */}
                            {item.type === 'conversation' && item.has_tool_calls && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-xs text-white font-bold">{item.tool_call_count}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Item information */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between">
                              <div className="truncate flex items-center gap-1.5 min-w-0 flex-1">
                                <span className={`text-sm font-medium truncate ${
                                  (() => {
                                    if (item.type === 'call') {
                                      if (item.call_category === 'action_required') {
                                        return areAllTasksCompleted(item) ? 'text-gray-500' : 'text-gray-900';
                                      }
                                      return item.viewed ? 'text-gray-500' : 'text-gray-900';
                                    } else {
                                      return item.viewed ? 'text-gray-500' : 'text-gray-900';
                                    }
                                  })()
                                }`}>
                                  {item.type === 'call' 
                                    ? getDisplayName(item)
                                    : getConversationDisplayName(item)
                                  }
                                </span>
                                {item.pinned && (
                                  <PushPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                )}
                              </div>
                              
                              <div className={`flex items-center text-xs ml-2 flex-shrink-0 ${
                                (() => {
                                  if (item.type === 'call') {
                                    if (item.call_category === 'action_required') {
                                      return areAllTasksCompleted(item) ? 'text-gray-400' : 'text-gray-500';
                                    }
                                    return item.viewed ? 'text-gray-400' : 'text-gray-500';
                                  } else {
                                    return item.viewed ? 'text-gray-400' : 'text-gray-500';
                                  }
                                })()
                              }`}>
                                <span>{formatCallDate(item.sort_date)}</span>
                              </div>
                            </div>
                            
                            <div className="mt-0.5">
                              {item.type === 'call' ? (
                                item?.summary && (
                                  <span className={`text-xs line-clamp-1 ${
                                    (() => {
                                      if (item.call_category === 'action_required') {
                                        return areAllTasksCompleted(item) ? 'text-gray-400' : 'text-gray-500';
                                      }
                                      return item.viewed ? 'text-gray-400' : 'text-gray-500';
                                    })()
                                  }`}>
                                    {item.summary.length > getResponsiveCharLimit() 
                                      ? `${item.summary.substring(0, getResponsiveCharLimit())}...` 
                                      : item.summary}
                                  </span>
                                )
                              ) : (
                                <>
                                  {item?.last_message && (
                                  <span className={`text-xs line-clamp-1 ${
                                    item.viewed ? 'text-gray-400' : 'text-gray-500'
                                  }`}>
                                    {item.last_message.length > getResponsiveCharLimit() 
                                      ? `${item.last_message.substring(0, getResponsiveCharLimit())}...` 
                                      : item.last_message}
                                  </span>
                                  )}
                                  
                                  {/* NEW: Tool call summary below message preview */}
                                  {item.has_tool_calls && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <div className="w-3 h-3 rounded-full bg-blue-100 flex items-center justify-center">
                                        <Lightning className="w-2 h-2 text-blue-600" />
                                      </div>
                                      <span className="text-xs text-blue-600 font-medium">
                                        {item.tool_call_count} AI tool{item.tool_call_count !== 1 ? 's' : ''} used
                                      </span>
                                      {item.last_tool_call && (
                                        <span className="text-xs text-gray-400">
                                          • {item.last_tool_call.name}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Right arrow indicator */}
                          <div className="flex-shrink-0">
                            <CaretRight className="h-4 w-4 text-gray-300 transition-colors duration-200" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Loading skeleton items */}
                  {isLoadingMore && (
                    <div className="divide-y divide-gray-50">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <SkeletonItem key={`skeleton-mobile-${i}`} />
                      ))}
                    </div>
                  )}
                  
                  {/* End of list indicator - mobile */}
                  {!hasMoreData && displayItems.length > 0 && (
                    <div className="py-4 text-center border-t border-gray-100">
                      <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                        <Check className="w-3 h-3 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-400">You've reached the end</p>
                    </div>
                  )}
                  
                  {/* Intersection observer target - mobile */}
                  {hasMoreData && (
                    <div 
                      ref={loadMoreRef} 
                      className="h-4 w-full flex items-center justify-center py-1"
                      style={{ minHeight: '16px' }}
                    >
                      <div className="w-1 h-1 bg-gray-200 rounded-full opacity-30" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu.visible && (
          <motion.div
            ref={contextMenuRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
                            className="absolute z-50 bg-white rounded-lg border border-gray-200 shadow-lg py-1 min-w-[160px]"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
            }}
          >
            <button
              onClick={() => {
                const item = contextMenu.item;
                handleContextAction(item.pinned ? 'unpin' : 'pin', item);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              {contextMenu.item?.pinned ? (
                <PushPinSlash className="w-4 h-4" />
              ) : (
                <PushPin className="w-4 h-4" />
              )}
              {contextMenu.item?.pinned ? 'Unpin' : 'Pin'}
            </button>
            <button
              onClick={() => {
                const item = contextMenu.item;
                if (item?.type === 'call') {
                  if (item.call_category === 'action_required') {
                    // Check if all tasks are completed
                    const allCompleted = areAllTasksCompleted(item);
                    handleContextAction(allCompleted ? 'mark_unviewed' : 'mark_viewed', item);
                  } else {
                    // For regular calls, toggle based on viewed status
                    handleContextAction(item.viewed ? 'mark_unviewed' : 'mark_viewed', item);
                  }
                } else if (item?.type === 'conversation') {
                  // For conversations, toggle based on viewed status
                  handleContextAction(item.viewed ? 'mark_unviewed' : 'mark_viewed', item);
                }
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <ArrowsClockwise className="w-4 h-4" />
              {(() => {
                const item = contextMenu.item;
                if (item?.type === 'call') {
                  if (item.call_category === 'action_required') {
                    // Check if all tasks are completed
                    const allCompleted = areAllTasksCompleted(item);
                    return allCompleted ? 'Mark Uncompleted' : 'Mark Completed';
                  } else {
                    // For regular calls, show opposite of current viewed status
                    return item.viewed ? 'Mark Unviewed' : 'Mark Viewed';
                  }
                } else if (item?.type === 'conversation') {
                  // For conversations, show opposite of current viewed status
                  return item.viewed ? 'Mark Unviewed' : 'Mark Viewed';
                }
                return 'Mark Unviewed';
              })()}
            </button>
            <button
              onClick={() => handleContextAction('view', contextMenu.item)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <CaretRight className="w-4 h-4" />
              View Details
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Panel */}
      <HelpPanel
        isOpen={helpPanel.isOpen}
        onClose={helpPanel.close}
        title={helpPanel.config.title}
        content={helpPanel.config.content}
        sections={helpPanel.config.sections}
        position={helpPanel.config.position}
        size={helpPanel.config.size}
      />
    </div>
    </>
  );
};

export default CallsListPage;